import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, X, SlidersHorizontal, Eye, Loader2, PackageX, User, Building2, MapPin, ArrowUpDown, ArrowUp, ArrowDown, Filter, Download, History } from 'lucide-react';
import { api } from '../api';
import { Card, CardContent, Badge, Button, cn } from '../../ui';
import * as XLSX from 'xlsx';
import { getIcon } from '../icons';
import { useTiposNavegables } from '../hooks/useTiposNavegables';
import { rutaTipo, normAmbito, etiquetaAmbito } from '../utils/rutaTipo';
import { useActivos, createActivo } from '../hooks/useActivos';
import { useActivoDetalle } from '../hooks/useActivoDetalle';
import { useInvPermisos } from '../InventarioAccess';
import EstadoBadge from '../components/EstadoBadge';
import Paginacion from '../components/Paginacion';
import Drawer from '../components/Drawer';
import ActivoForm from '../components/ActivoForm';
import HistorialActivo from '../components/HistorialActivo';
import { camposDeUbicacion } from '../utils/ubicacion';
import AccionesActivo from '../components/AccionesActivo';
import ConfirmDialog from '../components/ConfirmDialog';
import { formatNumberField } from '../utils/formatNumberField';

// Formateador para el campo `coste` en el detalle del activo: € con
// miles y 2 decimales (céntimos). El KPI del dashboard usa otro
// formateador sin decimales (FMT_EUR0) — son intencionadamente distintos.
const FMT_EUR2 = new Intl.NumberFormat('es-ES', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 2,
});

/**
 * Página: Lista de activos de un tipo concreto (sub-bloque III.5)
 *
 * Sustituye el placeholder de III.1. El tipo se resuelve por el PAR
 * (ámbito, slug): el ámbito viene del segmento de ruta (prop `scope`,
 * puesta en routes.jsx) y el slug del :slug de la URL.
 *
 * NO se deduce el ámbito del tipo, que era el bug: el slug no es clave
 * (inv_asset_types es UNIQUE (scope, slug)), así que un `find` por slug
 * devolvía uno de los dos gemelos de forma indeterminada y con él se
 * pintaba el listado, se exportaba y —lo peor— se daba de alta. Un alta
 * lanzada desde la entrada de menú de INTERNO acababa en la tabla de
 * colaboradores, consumiendo el contador de referencias de ese otro tipo.
 *
 *   - Header con icono + nombre + total
 *   - Barra: búsqueda + filtros (estado, empresa)
 *   - Tabla: referencia + columnas dinámicas del schema + estado +
 *     asignado + empresa + acción "ver"
 *   - Click en fila → Drawer de detalle + timeline + acciones (III.6)
 *   - Paginación Stripe-style
 *   - Soporta deep-link /inventario/:slug/:id (drawer abierto directo)
 *
 * El drawer integra: detalle de campos + meta + timeline de eventos
 * (componente Timeline) y barra de acciones contextual según estado
 * (componente AccionesActivo) en el footer.
 *
 * Dependencias ya aplicadas: III.4 (useTiposCrud, tiposMock).
 */

const MAX_TABLE_COLS = 4; // columnas dinámicas visibles; resto en el drawer

// Alias enriquecido que añade el backend (scope-2a) por tipo de vínculo
const LINK_NOMBRE_KEY = {
  user_link: 'usuario_nombre',
  company_link: 'empresa_nombre',
  empresa_link: 'empresa_nombre',
  office_link: 'oficina_nombre',
  business_link: 'negocio_nombre',
};

function formatValue(field, value, row) {
  // Campos de vínculo: mostrar el nombre enriquecido, no el id crudo
  const nombreKey = LINK_NOMBRE_KEY[field.type];
  if (nombreKey && row && row[nombreKey]) return String(row[nombreKey]);
  if (value === null || value === undefined || value === '') return '—';
  if (field.type === 'boolean') return value ? 'Sí' : 'No';
  // number/decimal con miles activo → formato es-ES (1.234, 1.234.567,5).
  // Sin miles, mostramos el valor tal cual para no romper casos donde el
  // separador no procede (años, 4 últimos dígitos de tarjeta, etc.).
  if (field.type === 'number' || field.type === 'decimal') {
    return formatNumberField(value, { miles: !!field.miles });
  }
  return String(value);
}

// Iniciales para el avatar del header (máx 2)
function iniciales(nombre) {
  const parts = String(nombre || '').trim().split(/\s+/).filter(Boolean);
  return (parts.slice(0, 2).map((p) => p[0]).join('') || '?').toUpperCase();
}

// Campos del schema que NO son vínculo (los *_link van a Vinculaciones)
function getCamposDatos(schema) {
  return (schema || []).filter((f) => !LINK_NOMBRE_KEY[f.type]);
}

// Subtítulo del header: primeros campos de texto con valor
function getHeaderSubtitle(activo, schema) {
  if (!activo) return '';
  const vals = (schema || [])
    .filter((f) => ['string', 'text', 'select', 'number', 'decimal'].includes(f.type))
    .map((f) => activo[f.name])
    .filter((v) => v !== null && v !== undefined && v !== '')
    .slice(0, 3);
  return vals.join(' · ');
}

// Vinculaciones estándar (las que el backend enriquece): usuario,
// empresa/colaboradora, oficina. Solo se muestran las que tienen valor.
function getVinculaciones(activo, empresaKey, scope) {
  if (!activo) return [];
  const out = [];
  if (activo.usuario_nombre) {
    out.push({ label: 'Usuario asignado', value: activo.usuario_nombre, Icon: User });
  }
  if (activo[empresaKey]) {
    out.push({
      label: scope === 'colab' ? 'Colaboradora' : 'Empresa',
      value: activo[empresaKey],
      Icon: Building2,
    });
  }
  if (activo.oficina_nombre) {
    out.push({ label: 'Oficina', value: activo.oficina_nombre, Icon: MapPin });
  }
  return out;
}

export default function ActivosList({ scope: scopeRuta }) {
  const { slug, id } = useParams();
  const navigate = useNavigate();

  // El ámbito manda, y viene de la URL. Se normaliza una sola vez aquí.
  const scope = normAmbito(scopeRuta);

  // Tipos NAVEGABLES (Capa 2): resuelve el tipo por (ámbito, slug) usando
  // la lista ya filtrada por can_view del usuario. Cubre roles sin
  // tipos.view. `buscarTipo` es el único sitio que hace ese emparejamiento.
  const { buscarTipo, loading: tiposLoading } = useTiposNavegables();
  const tipo = useMemo(() => buscarTipo(scope, slug), [buscarTipo, scope, slug]);
  // ¿Puede editar este tipo? Lo calcula el servidor con la MISMA regla que decide el 201/403 de
  // las escrituras (`typeAccessService.accesoPorTipo`). Aquí sólo oculta: «+ Añadir activo», el pie
  // de acciones y la caja de nota. Quien decide sigue siendo el servidor (19/10, Fase 2).
  const puedeEditar = !!tipo?.puede_editar;

  // Estado de listado
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroEmpresa, setFiltroEmpresa] = useState('');
  // Ordenación y filtros por columna (nuevas features)
  const [orderBy, setOrderBy] = useState('');     // '' = orden por defecto del backend (id DESC)
  const [orderDir, setOrderDir] = useState('asc'); // ignorado si orderBy === ''
  const [filtrosColumna, setFiltrosColumna] = useState({}); // { [colname]: value }
  const [mostrarFiltrosColumna, setMostrarFiltrosColumna] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [errorExportar, setErrorExportar] = useState(null); // aviso en diálogo, no window.alert

  // Debounce de búsqueda
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const empresaKey = scope === 'colab' ? 'empresa_colaboradora_nombre' : 'empresa_nombre';
  const filters = useMemo(() => {
    const f = {};
    if (filtroEstado) f.estado = filtroEstado;
    if (filtroEmpresa) f[empresaKey] = filtroEmpresa;
    // Filtros por columna (campos del schema y campos fijos). Solo
    // se incluyen los que tienen valor para no contaminar la URL.
    for (const [k, v] of Object.entries(filtrosColumna)) {
      if (v !== undefined && v !== null && String(v).trim() !== '') {
        f[k] = v;
      }
    }
    // Orden: viajan como query params normales; el backend los extrae
    // antes de tratar el resto como filtros.
    if (orderBy) {
      f.orderBy = orderBy;
      f.orderDir = orderDir;
    }
    return f;
  }, [filtroEstado, filtroEmpresa, empresaKey, filtrosColumna, orderBy, orderDir]);

  // Click en cabecera para ordenar: cicla asc → desc → asc (limpiar
  // orden = botón "Limpiar filtros" o pulsar otra columna).
  function toggleSort(col) {
    if (orderBy !== col) {
      setOrderBy(col);
      setOrderDir('asc');
    } else {
      setOrderDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    }
    setPage(1);
  }
  function sortIconFor(col) {
    if (orderBy !== col) return <ArrowUpDown className="w-3 h-3 ml-1 text-slate-300 inline" />;
    return orderDir === 'asc' ? (
      <ArrowUp className="w-3 h-3 ml-1 text-[hsl(var(--brand-light))] inline" />
    ) : (
      <ArrowDown className="w-3 h-3 ml-1 text-[hsl(var(--brand-light))] inline" />
    );
  }
  function patchFiltroColumna(col, value) {
    setFiltrosColumna((prev) => ({ ...prev, [col]: value }));
    setPage(1);
  }

  const { data, pagination, loading, error, isMock, refresh: refreshLista } = useActivos({
    slug,
    scope,
    page,
    pageSize,
    filters,
    search,
  });

  // Drawer de detalle: solo guardamos el id; el hook carga activo+eventos
  const [drawerId, setDrawerId] = useState(null);
  const [detalleTab, setDetalleTab] = useState('info');
  const {
    activo: drawerActivo,
    eventos,
    usuarios,
    empresas,
    negocios,
    oficinas,
    loading: detalleLoading,
    busy: accionBusy,
    ejecutarAccion,
    anadirNota,
    editarNota,
    borrarNota,
    refresh: refreshDetalle,
  } = useActivoDetalle({ slug, scope, id: drawerId, conCatalogos: puedeEditar });

  // Permiso para gestionar la fila histórica (editar fecha / soft-delete).
  // El backend protege PUT/DELETE /eventos/:id con `inventario.eventos.manage`;
  // aquí decidimos pintar los botones de la fila del Timeline solo si el
  // usuario también lo tiene (Super Admin lo bypassa vía useInvPermisos).
  const { can: canPerm } = useInvPermisos();
  const canManageEventos = canPerm('inventario.eventos.manage');
  // «Ver en el historial» (Fase 3c): el historial global exige eventos.view (InvGuard y servidor).
  const canVerHistorial = canPerm('inventario.eventos.view');

  // Editar la fecha de un evento: PUT /eventos/:id { fecha, edit_reason }.
  // edit_reason es obligatorio en backend → el modal del Timeline ya lo
  // pide; aquí lo enviamos tal cual. Tras guardar, recargamos detalle +
  // timeline para que la fecha nueva quede pintada.
  const onEditarFechaEvento = async (eventoId, nuevaFecha, motivo) => {
    await api.put(`/eventos/${eventoId}`, {
      fecha: nuevaFecha,
      edit_reason: motivo,
    });
    await refreshDetalle();
  };

  // Soft-delete del evento: DELETE /eventos/:id con body { delete_reason }.
  // El wrapper expone esto como api.del(path, body). No altera el estado
  // del activo (lo gestiona el backend); solo oculta la fila del Timeline.
  const onEliminarEvento = async (eventoId, motivo) => {
    await api.del(`/eventos/${eventoId}`, { delete_reason: motivo });
    await refreshDetalle();
  };

  const openDrawer = (activoId) => {
    setDrawerId(Number(activoId));
    navigate(rutaTipo(scope, slug, activoId), { replace: false });
  };
  const closeDrawer = () => {
    setDrawerId(null);
    navigate(rutaTipo(scope, slug), { replace: false });
  };

  // Tras una acción: refresca activo+timeline (lo hace el hook) y la lista
  const handleAccion = async (tipo, payload) => {
    await ejecutarAccion(tipo, payload);
    refreshLista();
  };

  // ─────────────────────────────────────────────────────────────────
  // Exportar a Excel (XLSX nativo con SheetJS).  Pide al backend
  // todos los activos con los filtros y orden actuales (paginación
  // grande para no truncar) y genera un .xlsx con tipos preservados:
  // los números van como número, las fechas como fecha, los textos
  // como texto. Excel lo abre directo con doble click.
  //
  // Requiere `npm i xlsx` en el front (SheetJS, ~600KB minified).
  // ─────────────────────────────────────────────────────────────────
  async function exportarXLSX() {
    if (!tipo) return;
    setExportando(true);
    try {
      const params = {
        page: 1,
        pageSize: 10000,
        scope: scope === 'colab' ? 'colab' : 'interno',
        ...(search ? { search } : {}),
        ...filters,
      };
      const res = await api.get(`/activos/${slug}`, params);
      const rows = res?.data || [];

      // Columnas: referencia + dinámicas (con nombres enriquecidos
      // para los *_link, igual que la tabla visible) + estado.
      const cols = [
        { key: 'Referencia', render: (r) => r.referencia || '' },
      ];
      for (const f of schema) {
        const header = f.label || f.name;
        const isLink = LINK_NOMBRE_KEY[f.type];
        cols.push({
          key: header,
          render: (r) => {
            if (isLink && r[isLink]) return String(r[isLink]);
            const v = r[f.name];
            if (v === null || v === undefined || v === '') return '';
            // SheetJS preserva tipos JS: dejamos numbers como number
            // y dates como string YYYY-MM-DD; Excel los reconoce.
            if (f.type === 'number') return Number(v);
            if (f.type === 'decimal') return Number(v);
            return String(v);
          },
        });
      }
      cols.push({ key: 'Estado', render: (r) => r.estado || '' });

      // Construir array de objetos { Header: value, ... }
      const data = rows.map((r) => {
        const obj = {};
        for (const c of cols) obj[c.key] = c.render(r);
        return obj;
      });

      // Crear hoja, libro y descargar
      const ws = XLSX.utils.json_to_sheet(data, {
        header: cols.map((c) => c.key),
      });

      // Anchos automáticos básicos según el contenido más largo de
      // cada columna (mejora la lectura nada más abrir el archivo).
      ws['!cols'] = cols.map((c) => {
        const maxLen = Math.max(
          c.key.length,
          ...data.map((r) => String(r[c.key] ?? '').length)
        );
        return { wch: Math.min(Math.max(maxLen + 2, 10), 50) };
      });

      const wb = XLSX.utils.book_new();
      const sheetName = (tipo.nombre || slug).slice(0, 31); // límite Excel
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      const stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `inventario_${slug}_${scope}_${stamp}.xlsx`);
    } catch (err) {
      setErrorExportar(err?.response?.data?.error || err?.message || String(err));
    } finally {
      setExportando(false);
    }
  }

  // Deep-link: si la URL trae :id, abrir el drawer al cargar
  useEffect(() => {
    if (id && !drawerId && tipo) {
      setDrawerId(Number(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, tipo]);

  // Columnas dinámicas a mostrar.
  // Si el tipo tiene campos marcados con show_in_list === true, se
  // muestran exactamente esos (en el orden del schema). Si NINGÚN campo
  // está marcado (tipos creados antes de esta feature), se mantiene el
  // comportamiento anterior: los primeros MAX_TABLE_COLS campos.
  const schema = tipo?.fields_schema || [];
  const marcados = schema.filter((f) => f.show_in_list === true);
  const tableCols =
    marcados.length > 0 ? marcados : schema.slice(0, MAX_TABLE_COLS);

  // Opciones de filtro de empresa (de los datos cargados)
  const empresasDisponibles = useMemo(() => {
    const set = new Set();
    data.forEach((r) => r[empresaKey] && set.add(r[empresaKey]));
    return [...set].sort();
  }, [data, empresaKey]);

  const Icon = tipo ? getIcon(tipo.icon) : null;

  if (tiposLoading) {
    return (
      <div className="px-6 py-5">
        <Card>
          <CardContent className="p-4 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Cargando catálogo…
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tipo) {
    return (
      <div className="px-6 py-5">
        <Card>
          <CardContent className="p-8 text-center">
            <PackageX className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">
              El tipo "{slug}" no existe en el ámbito{' '}
              {etiquetaAmbito(scope)} del catálogo, o no tienes acceso a él.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-6 py-5">
      {/* HEADER */}
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 leading-tight flex items-center gap-2">
            {Icon && <Icon className="w-5 h-5 text-[hsl(var(--brand-light))]" />}
            {tipo.nombre}
            <Badge variant={scope === 'colab' ? 'purple' : 'secondary'}>
              {etiquetaAmbito(scope)}
            </Badge>
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {tipo.categoria && tipo.categoria.nombre ? `${tipo.categoria.nombre} · ` : ''}
            {pagination.total} {pagination.total === 1 ? 'unidad' : 'unidades'}
            {isMock && (
              <span className="ml-2 text-[11px] text-amber-600 font-medium">
                · datos de demostración (backend pendiente)
              </span>
            )}
          </p>
        </div>
        {puedeEditar && (
          <Button onClick={() => setAddOpen(true)} className="shrink-0">
            + Añadir activo
          </Button>
        )}
      </div>

      {/* BARRA DE FILTROS */}
      <Card className="mb-3">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Buscar por referencia o cualquier campo…"
                className="w-full border border-slate-200 rounded-md pl-9 pr-9 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label="Limpiar búsqueda"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <select
              value={filtroEstado}
              onChange={(e) => {
                setFiltroEstado(e.target.value);
                setPage(1);
              }}
              className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
            >
              <option value="">Todos los estados</option>
              <option value="disponible">Disponible</option>
              <option value="asignado">Asignado</option>
              <option value="reparacion">En reparación</option>
              <option value="baja">De baja</option>
            </select>

            <select
              value={filtroEmpresa}
              onChange={(e) => {
                setFiltroEmpresa(e.target.value);
                setPage(1);
              }}
              className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
            >
              <option value="">
                {scope === 'colab' ? 'Todas las colaboradoras' : 'Todas las empresas'}
              </option>
              {empresasDisponibles.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>

            <button
              onClick={() => setMostrarFiltrosColumna((v) => !v)}
              className={cn(
                'flex items-center gap-1 text-xs px-2.5 py-2 border rounded-md',
                mostrarFiltrosColumna || Object.values(filtrosColumna).some((v) => v)
                  ? 'border-[hsl(var(--brand-light))] text-[hsl(var(--brand-light))] bg-blue-50'
                  : 'border-slate-200 text-slate-600 hover:text-slate-900'
              )}
              title="Filtros por columna"
            >
              <Filter className="w-3.5 h-3.5" />
              Filtros por columna
            </button>

            <button
              onClick={exportarXLSX}
              disabled={exportando || loading}
              className="flex items-center gap-1 text-xs px-2.5 py-2 border border-slate-200 rounded-md text-slate-600 hover:text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Descargar listado actual (con filtros aplicados) en Excel (.xlsx)"
            >
              {exportando ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Download className="w-3.5 h-3.5" />
              )}
              Exportar
            </button>

            {(filtroEstado || filtroEmpresa || search || orderBy || Object.values(filtrosColumna).some((v) => v)) && (
              <button
                onClick={() => {
                  setFiltroEstado('');
                  setFiltroEmpresa('');
                  setSearchInput('');
                  setFiltrosColumna({});
                  setOrderBy('');
                  setOrderDir('asc');
                  setPage(1);
                }}
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 px-2 py-2"
              >
                <SlidersHorizontal className="w-3.5 h-3.5" />
                Limpiar filtros
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* TABLA */}
      <Card>
        {error && (
          <CardContent className="p-4 text-sm text-rose-600">
            Error al cargar activos: {error.message}
          </CardContent>
        )}

        {!error && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-500 bg-slate-50 border-b border-slate-100">
                  <th
                    onClick={() => toggleSort('referencia')}
                    className="px-4 py-2.5 text-left font-medium cursor-pointer select-none hover:bg-slate-100"
                    title="Ordenar por Referencia"
                  >
                    Referencia
                    {sortIconFor('referencia')}
                  </th>
                  {tableCols.map((c) => (
                    <th
                      key={c.name}
                      onClick={() => toggleSort(c.name)}
                      className="px-4 py-2.5 text-left font-medium cursor-pointer select-none hover:bg-slate-100"
                      title={`Ordenar por ${c.label || c.name}`}
                    >
                      {c.label || c.name}
                      {sortIconFor(c.name)}
                    </th>
                  ))}
                  <th
                    onClick={() => toggleSort('estado')}
                    className="px-4 py-2.5 text-left font-medium cursor-pointer select-none hover:bg-slate-100"
                    title="Ordenar por Estado"
                  >
                    Estado
                    {sortIconFor('estado')}
                  </th>
                  <th className="px-4 py-2.5 text-right font-medium w-16"></th>
                </tr>
                {/* Fila de filtros por columna (toggle) */}
                {mostrarFiltrosColumna && (
                  <tr className="text-xs bg-blue-50/40 border-b border-slate-100">
                    <th className="px-3 py-1.5">
                      <input
                        value={filtrosColumna.referencia || ''}
                        onChange={(e) => patchFiltroColumna('referencia', e.target.value)}
                        placeholder="filtrar…"
                        className="w-full border border-slate-200 rounded px-2 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-300"
                      />
                    </th>
                    {tableCols.map((c) => (
                      <th key={c.name} className="px-3 py-1.5">
                        {c.type === 'date' ? (
                          <div className="flex flex-col gap-0.5">
                            <label className="flex items-center gap-1">
                              <span className="text-[9px] uppercase tracking-wide text-slate-400 w-8 shrink-0">
                                desde
                              </span>
                              <input
                                type="date"
                                value={filtrosColumna[`${c.name}__gte`] || ''}
                                onChange={(e) => patchFiltroColumna(`${c.name}__gte`, e.target.value)}
                                title="Desde (incluida)"
                                className="w-full border border-slate-200 rounded px-1.5 py-1 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-blue-300"
                              />
                            </label>
                            <label className="flex items-center gap-1">
                              <span className="text-[9px] uppercase tracking-wide text-slate-400 w-8 shrink-0">
                                hasta
                              </span>
                              <input
                                type="date"
                                value={filtrosColumna[`${c.name}__lte`] || ''}
                                onChange={(e) => patchFiltroColumna(`${c.name}__lte`, e.target.value)}
                                title="Hasta (incluida)"
                                className="w-full border border-slate-200 rounded px-1.5 py-1 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-blue-300"
                              />
                            </label>
                          </div>
                        ) : c.type === 'select' && Array.isArray(c.options) ? (
                          // Un select se filtra eligiendo una de SUS opciones (igualdad exacta en el
                          // servidor), no escribiendo: vale para cualquier tipo, p. ej. la provincia.
                          <select
                            value={filtrosColumna[c.name] || ''}
                            onChange={(e) => patchFiltroColumna(c.name, e.target.value)}
                            className="w-full border border-slate-200 rounded px-1.5 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-300"
                          >
                            <option value="">todas</option>
                            {c.options.map((o) => <option key={o} value={o}>{o}</option>)}
                          </select>
                        ) : (
                          <input
                            value={filtrosColumna[c.name] || ''}
                            onChange={(e) => patchFiltroColumna(c.name, e.target.value)}
                            placeholder="filtrar…"
                            className="w-full border border-slate-200 rounded px-2 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-300"
                          />
                        )}
                      </th>
                    ))}
                    <th className="px-3 py-1.5">
                      {/* Filtro de estado intencionalmente omitido —
                          ya existe el dropdown "Todos los estados" en
                          la barra superior; un input LIKE aquí sería
                          redundante y confuso. */}
                    </th>
                    <th></th>
                  </tr>
                )}
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={tableCols.length + 3} className="px-4 py-10 text-center">
                      <Loader2 className="w-5 h-5 animate-spin text-slate-300 mx-auto" />
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={tableCols.length + 3}
                      className="px-4 py-10 text-center text-sm text-slate-500"
                    >
                      No hay activos que coincidan con los filtros.
                    </td>
                  </tr>
                ) : (
                  data.map((row) => {
                    // Distinguir a primera vista los activos dados de baja:
                    // fondo rojo muy suave y hover en rose-100 para que el
                    // tinte no se "borre" al pasar el ratón. El badge "Baja"
                    // sigue mostrándose en la columna de estado.
                    const esBaja = row.estado === 'baja';
                    return (
                    <tr
                      key={row.id}
                      onClick={() => openDrawer(row.id)}
                      className={cn(
                        'cursor-pointer',
                        esBaja
                          ? 'bg-rose-50 hover:bg-rose-100 text-slate-500'
                          : 'hover:bg-slate-50'
                      )}
                    >
                      <td className="px-4 py-3 font-mono text-slate-900">
                        {row.referencia}
                      </td>
                      {tableCols.map((c) => (
                        <td key={c.name} className="px-4 py-3 text-slate-700">
                          {formatValue(c, row[c.name], row)}
                        </td>
                      ))}
                      <td className="px-4 py-3">
                        <EstadoBadge estado={row.estado} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openDrawer(row.id);
                          }}
                          className="text-slate-400 hover:text-[hsl(var(--brand-light))] p-1"
                          aria-label="Ver detalle"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="px-3 border-t border-slate-100">
          <Paginacion
            page={pagination.page}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(1);
            }}
          />
        </div>
      </Card>

      {/* DRAWER ALTA DE ACTIVO (P2) */}
      <Drawer
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title={`Añadir ${tipo?.nombre || 'activo'}`}
        subtitle={etiquetaAmbito(scope)}
      >
        <ActivoForm
          tipo={tipo}
          scope={scope}
          onCancel={() => setAddOpen(false)}
          onCreated={() => {
            setAddOpen(false);
            refreshLista();
          }}
        />
      </Drawer>

      {/* DRAWER DETALLE — rediseño 6b (header rico + tabs + secciones) */}
      <Drawer bare open={!!drawerId} onClose={closeDrawer}>
        {detalleLoading && !drawerActivo ? (
          <div className="flex-1 flex items-center justify-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Cargando detalle…
          </div>
        ) : drawerActivo ? (
          <>
            {/* HEADER */}
            <div className="bg-slate-900 text-white px-6 pt-5 pb-4 shrink-0">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-300 min-w-0">
                  {Icon && <Icon className="w-4 h-4 shrink-0" />}
                  <span className="truncate">
                    {tipo?.nombre} · ID {drawerActivo.id}
                  </span>
                </div>
                <button
                  onClick={closeDrawer}
                  aria-label="Cerrar"
                  className="text-slate-300 hover:text-white -m-1 p-1 shrink-0"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <h2 className="text-2xl font-semibold mt-2 font-mono">
                {drawerActivo.referencia || '—'}
              </h2>
              {getHeaderSubtitle(drawerActivo, schema) && (
                <p className="text-sm text-slate-300 mt-1">
                  {getHeaderSubtitle(drawerActivo, schema)}
                </p>
              )}
              <div className="flex items-center gap-3 mt-3">
                <EstadoBadge estado={drawerActivo.estado} />
                {drawerActivo.usuario_nombre && (
                  <span className="flex items-center gap-2 text-sm text-slate-100">
                    <span className="w-6 h-6 rounded-full bg-white/15 grid place-items-center text-[11px] font-medium">
                      {iniciales(drawerActivo.usuario_nombre)}
                    </span>
                    {drawerActivo.usuario_nombre}
                  </span>
                )}
                {/* Fase 3c: el historial global ya filtrado por este activo, por URL (se puede
                    compartir). Sólo con eventos.view: sin él, el historial global da 403. */}
                {canVerHistorial && (
                  <button
                    type="button"
                    onClick={() => navigate(`/inventario/historial?${new URLSearchParams({
                      slug, tipo_scope: normAmbito(scope), asset_id: String(drawerActivo.id),
                    }).toString()}`)}
                    className="ml-auto flex items-center gap-1.5 text-xs text-slate-200 hover:text-white border border-white/20 hover:border-white/40 rounded-md px-2 py-1"
                  >
                    <History className="w-3.5 h-3.5" />
                    Ver en el historial
                  </button>
                )}
              </div>
            </div>

            {/* TABS */}
            <div className="flex gap-1 px-6 border-b border-slate-200 shrink-0">
              {[
                ['info', 'Información'],
                ['historial', 'Historial'],
              ].map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setDetalleTab(k)}
                  className={cn(
                    'px-3 py-2.5 text-sm font-medium border-b-2 -mb-px',
                    detalleTab === k
                      ? 'border-slate-900 text-slate-900'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* CONTENIDO */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {detalleTab === 'info' ? (
                <div className="space-y-6">
                  {getCamposDatos(schema).length > 0 && (
                    <section>
                      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
                        Datos
                      </h3>
                      <div className="grid grid-cols-2 gap-3">
                        {getCamposDatos(schema).map((f) => (
                          <div
                            key={f.name}
                            className="border border-slate-200 rounded-lg px-3 py-2"
                          >
                            <div className="text-[11px] uppercase tracking-wide text-slate-400">
                              {f.label || f.name}
                            </div>
                            <div className="text-sm text-slate-800 mt-0.5 break-words">
                              {formatValue(f, drawerActivo[f.name], drawerActivo)}
                            </div>
                          </div>
                        ))}
                        {/* Coste — columna fija del tipo. Detalle con 2
                            decimales (céntimos). Vacío → '—'. */}
                        {tipo?.tiene_coste && (
                          <div className="border border-slate-200 rounded-lg px-3 py-2">
                            <div className="text-[11px] uppercase tracking-wide text-slate-400">
                              Coste
                            </div>
                            <div className="text-sm text-slate-800 mt-0.5 break-words">
                              {drawerActivo.coste == null || drawerActivo.coste === ''
                                ? '—'
                                : FMT_EUR2.format(Number(drawerActivo.coste))}
                            </div>
                          </div>
                        )}
                      </div>
                    </section>
                  )}

                  {getVinculaciones(drawerActivo, empresaKey, scope).length > 0 && (
                    <section>
                      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
                        Vinculaciones
                      </h3>
                      <div className="space-y-2">
                        {getVinculaciones(drawerActivo, empresaKey, scope).map((v) => (
                          <div
                            key={v.label}
                            className="flex items-center gap-3 border border-slate-200 rounded-lg px-3 py-2.5"
                          >
                            <span className="w-9 h-9 rounded-full bg-slate-100 grid place-items-center text-slate-500 shrink-0">
                              <v.Icon className="w-4 h-4" />
                            </span>
                            <div className="min-w-0">
                              <div className="text-[11px] uppercase tracking-wide text-slate-400">
                                {v.label}
                              </div>
                              <div className="text-sm text-slate-800 truncate">
                                {v.value}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {getCamposDatos(schema).length === 0 &&
                    getVinculaciones(drawerActivo, empresaKey, scope).length === 0 && (
                      <p className="text-sm text-slate-500">
                        Sin datos para mostrar.
                      </p>
                    )}
                </div>
              ) : (
                <HistorialActivo
                  eventos={eventos}
                  puedeEditar={puedeEditar}
                  onAnadirNota={anadirNota}
                  onEditarNota={editarNota}
                  onBorrarNota={borrarNota}
                  tieneUbicacion={!!camposDeUbicacion(schema)}
                  onEditarFecha={canManageEventos ? onEditarFechaEvento : undefined}
                  onEliminar={canManageEventos ? onEliminarEvento : undefined}
                />
              )}
            </div>

            {/* FOOTER · acciones (lógica reconciliada en 6a-front). Sin can_edit no se pinta:
                todas sus acciones escriben y el servidor respondería 403. */}
            {puedeEditar && (
              <div className="border-t border-slate-200 px-6 py-3 bg-slate-50 shrink-0">
                <AccionesActivo
                  activo={drawerActivo}
                  schema={schema}
                  usuarios={usuarios}
                  empresas={empresas}
                  negocios={negocios}
                  oficinas={oficinas}
                  busy={accionBusy}
                  onAccion={handleAccion}
                  tieneCoste={!!tipo?.tiene_coste}
                />
              </div>
            )}
          </>
        ) : null}
      </Drawer>

      <ConfirmDialog
        open={!!errorExportar}
        onClose={() => setErrorExportar(null)}
        titulo="No se pudo exportar"
      >
        <p>{errorExportar}</p>
      </ConfirmDialog>
    </div>
  );
}
